import React, { useEffect, useState } from "react";
import axios from "axios";
import "./PlansComponent.css";
import { API_URL } from "../../consts";

const PAYPAL_MODE = process.env.REACT_APP_PAYPAL_MODE || "production";
const PAYPAL_CLIENT_IDS = {
  sandbox: process.env.REACT_APP_PAYPAL_SANDBOX_CLIENT_ID || "YOUR_SANDBOX_CLIENT_ID",
  production: "ATyTWVR6hZumnFCIK2vi8KV6vZJlYIPvZ6-xkCM6G8QdLq2QiEZ9myl2S3xF-8q1rTWzz30EV7-D0JqT",
};

const PLAN_COLORS = ["#E8F5E9", "#E0F7FA", "#E3E0F7", "#FFF3E0"];
const PLAN_HIGHLIGHTS = ["#66BB6A", "#26C6DA", "#7E57C2", "#FF9800"];

export function formatPrice(price) {
  if (price == null || price === "") return "Free";
  const n = Number(price);
  if (Number.isNaN(n) || n === 0) return "Free";
  return `$${n}/month`;
}

export function normalizeBenefits(benefitsArray) {
  if (!benefitsArray) return [];
  if (Array.isArray(benefitsArray)) return benefitsArray.filter(Boolean).map(String);
  return [String(benefitsArray)];
}

export function formatExpirationDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Shared plans list + subscription UI.
 * Props:
 *   plans, plansLoading, plansError - data
 *   currentPlanName - string (user.plan_name or tenant?.plan) for "Current Plan" badge
 *   planType - 'consumer' | 'business'
 *   showRedeemCoupon - boolean (consumer only)
 *   showCancelSubscription - boolean (consumer only, when on paid plan)
 *   user - { plan_name, subscription_status, plan_expiration_date } for consumer subscription info
 *   showSandboxBanner, title, subtitle, showDisclaimer
 *   popularPlanName - e.g. 'Basic' for "Most Popular" badge
 *   wrapperClassName - optional wrapper class (e.g. 'plans-component' or inline)
 *   organizationId - when set (business flow), subscription is saved on tenant document via /plans/subscribe-organization
 */
const PERSONAL_SUB_CONFIRM_MESSAGE =
  "You currently have an active personal subscription. By subscribing your organization to a business plan, your personal subscription will be cancelled and you will be associated with the organization's plan instead.";

const PlansListBase = ({
  plans = [],
  plansLoading = false,
  plansError = null,
  currentPlanName = "",
  planType = "consumer",
  showRedeemCoupon = false,
  showCancelSubscription = false,
  user = null,
  showSandboxBanner = false,
  title = null,
  subtitle = null,
  showDisclaimer = false,
  popularPlanName = "Basic",
  wrapperClassName = "plans-component",
  organizationId = null,
}) => {
  const [loading, setLoading] = useState({});
  const [cancelling, setCancelling] = useState(false);
  const [dialog, setDialog] = useState({ show: false, type: "", title: "", message: "" });
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [personalSubCheck, setPersonalSubCheck] = useState({ hasActive: false, loading: true });
  const [confirmedPlanForPayPal, setConfirmedPlanForPayPal] = useState({});
  const [pendingOrgSubscribe, setPendingOrgSubscribe] = useState(null);

  const isConsumer = planType === "consumer";
  const currentNameLower = (currentPlanName || "").toLowerCase();

  const handleSubscriptionSuccess = async (subscriptionID, planName) => {
    setLoading((prev) => ({ ...prev, [planName]: true }));
    const isOrgSubscription = Boolean(organizationId);
    const url = isOrgSubscription ? `${API_URL}/plans/subscribe-organization` : `${API_URL}/plans/subscribe`;
    const payload = isOrgSubscription
      ? { organization_id: organizationId, name: planName, subscription_id: subscriptionID }
      : { name: planName, subscription_id: subscriptionID };
    try {
      const response = await axios.post(url, payload, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });
      let message = isOrgSubscription
        ? `Your organization has been subscribed to the ${planName} plan.`
        : `You have successfully subscribed to the ${planName} plan. Welcome to Datarate Premium!`;
      if (response.data?.owner_personal_subscription_cancelled) {
        message += " Your personal subscription has been cancelled.";
      }
      setDialog({
        show: true,
        type: "success",
        title: "Subscription Successful!",
        message,
      });
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      console.error("❌ Error updating plan:", error);
      setDialog({
        show: true,
        type: "error",
        title: "Subscription Error",
        message:
          "Your subscription was created successfully, but there was an error updating your account. Please contact our support team for assistance.",
      });
      setLoading((prev) => ({ ...prev, [planName]: false }));
    }
  };

  useEffect(() => {
    if (organizationId) {
      axios
        .get(`${API_URL}/plans/personal-subscription-status`, { withCredentials: true })
        .then(({ data }) => setPersonalSubCheck({ hasActive: !!data?.has_active_personal_subscription, loading: false }))
        .catch(() => setPersonalSubCheck({ hasActive: false, loading: false }));
    } else {
      setPersonalSubCheck({ hasActive: false, loading: false });
    }
  }, [organizationId]);

  useEffect(() => {
    if (!plans.length) return;
    const initializePayPalButtons = () => {
      setTimeout(() => {
        plans.forEach((plan) => {
          const paypalPlanId = plan.paypal_plan_id || plan.paypalPlanId;
          if (paypalPlanId && plan.name !== "Free") {
            const containerId = `paypal-button-container-${paypalPlanId}`;
            const container = document.getElementById(containerId);
            if (container && window.paypal) {
              while (container.firstChild) container.removeChild(container.firstChild);
              if (plan.name === "Test") {
                window.paypal
                  .Buttons({
                    style: { shape: "rect", color: "gold", layout: "vertical", label: "subscribe" },
                    createSubscription: (data, actions) =>
                      actions.subscription.create({ plan_id: paypalPlanId }),
                    onApprove: (data) => alert(data.subscriptionID),
                  })
                  .render(`#${containerId}`)
                  .catch((err) => console.error("❌ Failed to render PayPal button:", err));
              } else {
                window.paypal
                  .Buttons({
                    style: { shape: "rect", color: "blue", layout: "vertical", label: "subscribe" },
                    createSubscription: (data, actions) =>
                      actions.subscription.create({ plan_id: paypalPlanId }),
                    onApprove: (data) => handleSubscriptionSuccess(data.subscriptionID, plan.name),
                    onError: (err) => {
                      console.error("❌ PayPal subscription error:", err);
                      setDialog({
                        show: true,
                        type: "error",
                        title: "Payment Error",
                        message:
                          "There was an error processing your payment. Please try again or contact support if the problem persists.",
                      });
                    },
                    onCancel: () => {},
                  })
                  .render(`#${containerId}`)
                  .catch((err) => console.error("❌ Failed to render PayPal button:", err));
              }
            }
          }
        });
      }, 100);
    };
    if (window.paypal) {
      initializePayPalButtons();
    } else {
      const clientId = PAYPAL_CLIENT_IDS[PAYPAL_MODE];
      const existingScript = document.querySelector(`script[src*="paypal.com/sdk/js"]`);
      if (existingScript) {
        const checkPayPal = setInterval(() => {
          if (window.paypal) {
            clearInterval(checkPayPal);
            initializePayPalButtons();
          }
        }, 100);
        setTimeout(() => clearInterval(checkPayPal), 10000);
      } else {
        const script = document.createElement("script");
        script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&vault=true&intent=subscription&disable-funding=credit,card`;
        script.async = true;
        script.setAttribute("data-sdk-integration-source", "button-factory");
        document.body.appendChild(script);
        script.onload = () => initializePayPalButtons();
        script.onerror = () => console.error("❌ Failed to load PayPal SDK");
      }
    }
    const retryTimer = setTimeout(() => {
      if (window.paypal) initializePayPalButtons();
    }, 500);
    return () => {
      clearTimeout(retryTimer);
      plans.forEach((plan) => {
        const paypalPlanId = plan.paypal_plan_id || plan.paypalPlanId;
        if (paypalPlanId && plan.name !== "Free") {
          const container = document.getElementById(`paypal-button-container-${paypalPlanId}`);
          if (container) while (container.firstChild) container.removeChild(container.firstChild);
        }
      });
    };
  }, [plans, confirmedPlanForPayPal]);

  const showOrgSubscribeCustomButton = (plan) =>
    organizationId && (personalSubCheck.loading || (personalSubCheck.hasActive && !confirmedPlanForPayPal[plan.name]));

  const handleOrgSubscribeClick = (plan) => {
    setPendingOrgSubscribe(plan.name);
    setDialog({
      show: true,
      type: "confirmPersonalSub",
      title: "Personal subscription will be cancelled",
      message: PERSONAL_SUB_CONFIRM_MESSAGE,
    });
  };

  const confirmOrgSubscribeClick = () => {
    if (pendingOrgSubscribe) {
      setConfirmedPlanForPayPal((prev) => ({ ...prev, [pendingOrgSubscribe]: true }));
      setPendingOrgSubscribe(null);
    }
    setDialog({ show: false, type: "", title: "", message: "" });
  };

  const renderPayPalButton = (plan) => {
    const paypalPlanId = plan.paypal_plan_id || plan.paypalPlanId;
    if (!paypalPlanId || plan.name === "Free") return null;
    if (showOrgSubscribeCustomButton(plan)) {
      const isLoading = personalSubCheck.loading;
      const needsConfirm = personalSubCheck.hasActive && !confirmedPlanForPayPal[plan.name];
      return (
        <div style={{ marginTop: "10px", width: "100%" }}>
          <button
            type="button"
            className="plan-button"
            disabled={isLoading}
            style={{
              width: "100%",
              padding: "12px 24px",
              borderRadius: "8px",
              border: "none",
              background: "var(--accent-primary, #2563eb)",
              color: "#fff",
              fontWeight: 600,
              cursor: isLoading ? "wait" : "pointer",
              opacity: isLoading ? 0.7 : 1,
            }}
            onClick={() => needsConfirm && handleOrgSubscribeClick(plan)}
          >
            {isLoading ? "Checking..." : "Subscribe"}
          </button>
        </div>
      );
    }
    return (
      <div style={{ marginTop: "10px", width: "100%" }}>
        <div
          id={`paypal-button-container-${paypalPlanId}`}
          style={{
            minHeight: "45px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        />
        {loading[plan.name] && (
          <div style={{ textAlign: "center", marginTop: "10px", color: "#666" }}>
            Processing subscription...
          </div>
        )}
      </div>
    );
  };

  const handleCancelSubscription = () => {
    setDialog({
      show: true,
      type: "confirm",
      title: "Cancel Subscription",
      message:
        "Are you sure you want to cancel your subscription? This action cannot be undone and you will lose access to premium features at the end of your billing period.",
    });
  };

  const confirmCancelSubscription = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API_URL}/plans/cancel-subscription`, {}, { withCredentials: true, headers: { "Content-Type": "application/json" } });
      setDialog({
        show: true,
        type: "success",
        title: "Subscription Cancelled",
        message:
          "Your subscription has been cancelled successfully. You will retain access to premium features until the end of your current billing period.",
      });
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      console.error("❌ Error cancelling subscription:", error);
      const serverError = error.response?.data?.error;
      setDialog({
        show: true,
        type: "error",
        title: "Cancellation Error",
        message: serverError || "There was an error cancelling your subscription. Please contact our support team.",
      });
    } finally {
      setCancelling(false);
    }
  };

  const closeDialog = () => setDialog({ show: false, type: "", title: "", message: "" });
  const openCouponModal = () => {
    setCouponModalOpen(true);
    setCouponCode("");
    setCouponError("");
  };
  const closeCouponModal = () => {
    setCouponModalOpen(false);
    setCouponCode("");
    setCouponError("");
  };

  const handleRedeemCoupon = async () => {
    const code = (couponCode || "").trim();
    if (!code) {
      setCouponError("Please enter a coupon code.");
      return;
    }
    setCouponError("");
    setCouponLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/plans/redeem-coupon`,
        { code },
        { withCredentials: true, headers: { "Content-Type": "application/json" } }
      );
      closeCouponModal();
      setDialog({
        show: true,
        type: "success",
        title: "Coupon Redeemed!",
        message: `You received ${response.data.free_months} free month(s) of ${response.data.plan?.name || "premium"}. Your plan is active until ${response.data.plan?.expiration_date ? new Date(response.data.plan.expiration_date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : ""}.`,
      });
      setTimeout(() => window.location.reload(), 2000);
    } catch (err) {
      setCouponError(err.response?.data?.error || err.message || "Failed to redeem coupon.");
    } finally {
      setCouponLoading(false);
    }
  };

  const handleTermsClick = () => window.open("https://datarate.io/terms", "_blank");

  const isCurrentPlan = (plan) => {
    if (isConsumer && user) {
      return user.plan_name === plan.name && user.subscription_status !== "cancelled";
    }
    return (plan.name || "").toLowerCase() === currentNameLower;
  };

  return (
    <div className={wrapperClassName}>
      {dialog.show && (
        <div className="dialog-overlay" onClick={closeDialog}>
          <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
            <div className={`dialog-icon ${dialog.type}`}>
              {dialog.type === "success" && "✓"}
              {dialog.type === "error" && "✕"}
              {(dialog.type === "confirm" || dialog.type === "confirmPersonalSub") && "?"}
            </div>
            <h3 className="dialog-title">{dialog.title}</h3>
            <p className="dialog-message">{dialog.message}</p>
            <div className="dialog-buttons">
              {dialog.type === "confirm" ? (
                <>
                  <button className="dialog-button cancel" onClick={closeDialog}>
                    Cancel
                  </button>
                  <button
                    className="dialog-button confirm"
                    onClick={() => {
                      closeDialog();
                      confirmCancelSubscription();
                    }}
                  >
                    Yes, Cancel Subscription
                  </button>
                </>
              ) : dialog.type === "confirmPersonalSub" ? (
                <>
                  <button className="dialog-button cancel" onClick={closeDialog}>
                    Cancel
                  </button>
                  <button
                    className="dialog-button confirm"
                    onClick={confirmOrgSubscribeClick}
                  >
                    Confirm and subscribe
                  </button>
                </>
              ) : (
                <button className="dialog-button primary" onClick={closeDialog}>
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {couponModalOpen && (
        <div className="dialog-overlay" onClick={closeCouponModal}>
          <div className="dialog-content coupon-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "420px" }}>
            <h3 className="dialog-title">Redeem Coupon</h3>
            <p className="dialog-message" style={{ marginBottom: "1rem" }}>
              Enter your coupon code below.
            </p>
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              placeholder="Coupon code"
              disabled={couponLoading}
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: "6px",
                border: "1px solid #e5e7eb",
                fontSize: "1rem",
                marginBottom: "0.75rem",
                boxSizing: "border-box",
              }}
              onKeyDown={(e) => e.key === "Enter" && handleRedeemCoupon()}
            />
            {couponError && (
              <p style={{ color: "var(--error-color, #dc2626)", fontSize: "0.9rem", marginBottom: "0.75rem" }}>
                {couponError}
              </p>
            )}
            <div className="dialog-buttons" style={{ marginTop: "0.5rem" }}>
              <button className="dialog-button cancel" onClick={closeCouponModal} disabled={couponLoading}>
                Cancel
              </button>
              <button className="dialog-button primary" onClick={handleRedeemCoupon} disabled={couponLoading}>
                {couponLoading ? "Redeeming..." : "Redeem"}
              </button>
            </div>
          </div>
        </div>
      )}

      {title != null && <h1 className="plans-title">{title}</h1>}
      {subtitle != null && <p className="plans-subtitle">{subtitle}</p>}
      {plansLoading && (
        <p style={{ textAlign: "center", color: "var(--text-secondary)", marginBottom: "1rem" }}>
          Loading plans...
        </p>
      )}
      {plansError && (
        <p style={{ textAlign: "center", color: "var(--error-color)", marginBottom: "1rem" }}>{plansError}</p>
      )}
      {showSandboxBanner && (
        <div
          style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "4px",
            padding: "10px 15px",
            marginBottom: "20px",
            textAlign: "center",
            color: "#856404",
          }}
        >
          ⚠️ <strong>SANDBOX MODE</strong> - Test subscriptions only (no real charges)
        </div>
      )}
      <div className="plans-grid">
        {!plansLoading && plans.length === 0 && !plansError && (
          <p style={{ textAlign: "center", gridColumn: "1 / -1", color: "var(--text-secondary)" }}>
            No plans available.
          </p>
        )}
        {plans.map((plan, index) => {
          const isCurrent = isCurrentPlan(plan);
          const isPopular = (plan.name || "") === popularPlanName;
          const benefits = normalizeBenefits(plan.benefits_array);
          const color = plan.color || PLAN_COLORS[index % PLAN_COLORS.length];
          const highlight = plan.highlight || PLAN_HIGHLIGHTS[index % PLAN_HIGHLIGHTS.length];
          const paypalPlanId = plan.paypal_plan_id || plan.paypalPlanId;
          const showSubscribe = paypalPlanId && plan.name !== "Free";
          const showCancel = isConsumer && showCancelSubscription && isCurrent && user?.subscription_status === "active" && !user?.current_subscription_by_coupon;

          return (
            <div
              key={plan.id || index}
              className={`plan-card ${isCurrent ? "current-plan" : ""} ${isPopular ? "popular-plan" : ""}`}
              style={{ backgroundColor: color }}
            >
              {isPopular && <div className="popular-badge">Most Popular</div>}
              {isCurrent && <div className="current-badge">Current Plan</div>}
              <div className="plan-header">
                <div className="plan-icon-large">{(plan.name && plan.name[0]) || "?"}</div>
                <h2 className="plan-name" style={{ color: highlight }}>
                  {plan.name}
                </h2>
                <p className="plan-price">{formatPrice(plan.price)}</p>
              </div>
              <div className="plan-features-container">
                {benefits.length > 0 && (
                  <ul className="plan-features">
                    {benefits.map((feature, i) => (
                      <li key={i} className="feature-item">
                        <span className="feature-icon">✓</span>
                        <span className="feature-text">{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
      
                {isConsumer && isCurrent && user && (
                  <div className="subscription-info">
                    {user.subscription_status === "active" && user.plan_expiration_date && (
                      <div className="subscription-detail active">
                        <span className="subscription-icon">✓</span>
                        <span className="subscription-text">
                          Subscription active until {formatExpirationDate(user.plan_expiration_date)}
                        </span>
                      </div>
                    )}
                    {user.subscription_status === "cancelled" && user.plan_expiration_date && (
                      <div className="subscription-detail cancelled">
                        <span className="subscription-icon">!</span>
                        <span className="subscription-text">
                          Subscription cancelled - access until {formatExpirationDate(user.plan_expiration_date)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="plan-actions">
                {plan.name !== "Free" && (
                  <div>
                    {isCurrent ? (
                      isConsumer && showCancel ? (
                        <div className="current-plan-actions">
                          <button disabled className="plan-button current-plan-btn">
                            <span className="button-icon">✓</span>
                            Current Plan
                          </button>
                          <button
                            onClick={handleCancelSubscription}
                            disabled={cancelling}
                            className="plan-button cancel-btn"
                          >
                            <span className="button-icon">{cancelling ? "..." : "×"}</span>
                            {cancelling ? "Cancelling..." : "Cancel Subscription"}
                          </button>
                        </div>
                      ) : (
                        <button disabled className="plan-button current-plan-btn">
                          <span className="button-icon">✓</span>
                          Current Plan
                        </button>
                      )
                    ) : showSubscribe ? (
                      <div className="subscribe-section">{renderPayPalButton(plan)}</div>
                    ) : null}
                  </div>
                )}
                {plan.name === "Free" && (
                  <div className="free-plan-info">
                    <p className="free-plan-text">Perfect for getting started</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showRedeemCoupon && (
        <div className="redeem-coupon-section" style={{ marginTop: "1rem", marginBottom: "1rem", textAlign: "center" }}>
          <p style={{ marginBottom: "0.75rem", color: "var(--text-secondary, #6b7280)", fontSize: "1rem" }}>
            Have a coupon code?
          </p>
          <button
            type="button"
            onClick={openCouponModal}
            className="plan-button"
            style={{
              padding: "10px 24px",
              borderRadius: "8px",
              border: "1px solid var(--border-color, #e5e7eb)",
              background: "var(--background-primary, #fff)",
              color: "var(--accent-primary, #2563eb)",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Redeem Coupon
          </button>
        </div>
      )}

      {showDisclaimer && (
        <div className="plans-disclaimer">
          <p>
            By subscribing to any plan, you agree to our{" "}
            <span onClick={handleTermsClick} style={{ color: "#1976d2", textDecoration: "underline", cursor: "pointer" }}>
              Terms and Conditions
            </span>
            ,{" "}
            <span onClick={handleTermsClick} style={{ color: "#1976d2", textDecoration: "underline", cursor: "pointer" }}>
              Privacy Policy
            </span>
            , and{" "}
            <span onClick={handleTermsClick} style={{ color: "#1976d2", textDecoration: "underline", cursor: "pointer" }}>
              Refund Policy
            </span>
            .
          </p>
        </div>
      )}
    </div>
  );
};

export default PlansListBase;
